import SettingsSearchTarget from '@/components/Settings/SettingsSearchTarget';
import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import PropTypes from 'prop-types';

// Create styles outside component
const createStyles = (colors, isFirst, isLast, isColumn, isBold, subtitle, titleNote) => StyleSheet.create({
  section: {
    paddingTop: isFirst ? 4 : 20,
    paddingBottom: 20,
    borderBottomWidth: isLast ? 0 : 0.5,
    borderBottomColor: colors.border_color_secondary,
    flexDirection: isColumn ? 'column' : 'row',
    flexWrap: isColumn ? 'nowrap' : 'wrap',
    columnGap: 24,
    rowGap: 12,
    alignItems: isColumn ? 'flex-start' : 'center',
    justifyContent: isColumn ? 'flex-start' : 'space-between',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: isColumn ? undefined : 1,
    minWidth: isColumn ? undefined : 160,
    alignSelf: isColumn ? 'stretch' : undefined,
  },
  sectionTitle: {
    fontWeight: isBold ? '600' : '400',
    marginBottom: isColumn && !subtitle && !titleNote ? 15 : 0,
  },
  sectionTitleWithNote: {
    maxWidth: '100%',
  },
  sectionSubtitle: {
    color: colors.text_color_secondary,
    lineHeight: 20,
    marginTop: 4,
    marginBottom: isColumn && !!subtitle ? 15 : 0,
  },
  headerContainer: {
    width: '100%',
  },
});

// Memoized header component
const SectionHeader = React.memo(({ 
  title, 
  subtitle, 
  titleNote,
  styles 
}) => (
  <View style={styles.headerContainer}>
    <Text 
      size='n'
      style={[
        styles.sectionTitle,
        titleNote && styles.sectionTitleWithNote,
      ]}
      accessibilityRole="header"
    >
      {title}
      {titleNote != null && titleNote !== '' && (
        <Text size='n' color='tertiary'>
          {` - ${titleNote}`}
        </Text>
      )}
    </Text>
    {subtitle && (
      <Text 
        size='s'
        style={styles.sectionSubtitle}
        accessibilityRole="text"
      >
        {subtitle}
      </Text>
    )}
  </View>
));

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  titleNote: PropTypes.string,
  styles: PropTypes.object.isRequired,
};

SectionHeader.displayName = 'SectionHeader';

function SettingsSection({ 
  title, 
  isBold = false, 
  subtitle, 
  titleNote,
  isColumn = false, 
  isFirst = false, 
  isLast = false, 
  children 
}) {
  const { colors } = useTheme();

  // Memoize styles
  const styles = useMemo(() => 
    createStyles(colors, isFirst, isLast, isColumn, isBold, subtitle, titleNote),
    [colors, isFirst, isLast, isColumn, isBold, subtitle, titleNote]
  );

  return (
    <SettingsSearchTarget label={title}
      style={styles.section}
      accessibilityLabel={`${title} section`}
    >
      {
        (title || subtitle) && (
        <View style={styles.sectionHeader}>
          <SectionHeader
            title={title}
            subtitle={subtitle}
            titleNote={titleNote}
            styles={styles}
          />
        </View>
      )}
      {children}
    </SettingsSearchTarget>
  );
}

export default React.memo(SettingsSection);
